import React, { useState, useMemo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import dayjs from 'dayjs';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import LockIconWrapper from '../../../components/LockIconWrapper';
import { generateUniqueColor } from '../../../../util/colorUtil';
import { toast } from 'react-toastify';

function ResourceSection({ booking, hasUpdateRights, onUpdate, borderColor, onClose = null }) {
  const theme = useTheme();
  const { getAvailableResourcesForShift, validateResourceChange, updateInternalBooking } =
    useInternalBooking();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [availableResources, setAvailableResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingResource, setPendingResource] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);

  const canUpdateResource = useMemo(
    () => hasUpdateRights && !booking.finishedAt,
    [hasUpdateRights, booking.finishedAt]
  );

  const resourceColor = useMemo(() => {
    if (borderColor) return borderColor; // Use provided color if available
    if (booking?.resourceId) {
      const resourceId = booking.resourceId._id || booking.resourceId;
      return generateUniqueColor(resourceId.toString());
    }
    return '#808080'; // Return a default grey color
  }, [borderColor, booking?.resourceId?._id, booking?.entertainment]);

  const handleResourceClick = async () => {
    if (!canUpdateResource) return;

    setDialogOpen(true);
    setLoading(true);

    try {
      const resources = await getAvailableResourcesForShift(booking._id);
      const entertainment = booking?.entertainmentId && typeof booking.entertainmentId === 'object'
        ? booking.entertainmentId
        : null;
      const basicIds = Array.isArray(entertainment?.resourceIds)
        ? entertainment.resourceIds.map(id => (id ? String(id) : null))
        : [];
      const orderedIds =
        entertainment?.orderedResources && Array.isArray(entertainment.orderedResources.resources)
          ? entertainment.orderedResources.resources
              .map(r => (r && r.resourceId ? String(r.resourceId) : null))
              .filter(Boolean)
          : [];
      const allowedEntertainmentResourceIds = new Set(
        [...basicIds, ...orderedIds].filter(Boolean).map(String)
      );
      const filtered = Array.isArray(resources)
        ? allowedEntertainmentResourceIds.size > 0
          ? resources.filter(r => allowedEntertainmentResourceIds.has(String(r?._id)))
          : resources
        : [];
      setAvailableResources(filtered);
    } catch (error) {
      console.error('Error loading resources:', error);
      toast.error('Fehler beim Laden der verfügbaren Räume');
    } finally {
      setLoading(false);
    }
  };

  const handleResourceSelect = async resource => {
    setValidating(true);

    try {
      const validation = await validateResourceChange(booking._id, resource._id);

      if (validation.needsTimeAdjustment) {
        if (validation.hasConflict) {
          toast.error('Der neue Zeitslot für diese Ressource ist bereits belegt');
          setValidating(false);
          return;
        }

        setPendingResource(resource);
        setValidationResult(validation);
        setConfirmDialogOpen(true);
        setValidating(false);
      } else {
        setValidating(false);
        await performResourceUpdate(resource, null);
      }
    } catch (error) {
      console.error('Error validating resource change:', error);
      toast.error('Fehler beim Validieren der Raumänderung');
      setValidating(false);
    }
  };

  const performResourceUpdate = async (resource, adjustedStartTime) => {
    setUpdating(true);

    const timeChangeText = adjustedStartTime
      ? ` und die Startzeit wurde zu ${dayjs(adjustedStartTime).format('HH:mm')} angepasst`
      : '';

    const toastMessage = `Deine Anfrage für das Ändern des Raums${timeChangeText} für ${booking.firstName} ${booking.lastName} wurde erfolgreich gespeichert und kann später in den offenen Anfragen bestätigt werden`;
    const successToastMessage = `Der Raum für ${booking.firstName} ${booking.lastName} wurde erfolgreich zu ${resource.name} geändert${timeChangeText}`;

    const displayInfo = {
      Typ: 'Raum zuweisen',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Von: booking.resourceId?.name || 'Kein Raum',
      Nach: resource.name,
      Buchungsnummer: booking.bookingNumber,
    };

    if (adjustedStartTime) {
      displayInfo['Neue Startzeit'] = dayjs(adjustedStartTime).format('DD.MM.YYYY HH:mm');
    }

    const onSuccess = async () => {
      onUpdate(booking._id, { status: 'removed' });
    };

    try {
      const updateData = { resourceId: resource._id };
      if (adjustedStartTime) {
        updateData.adjustedStartTime = adjustedStartTime;
      }

      const updatedBooking = await updateInternalBooking(
        booking._id,
        updateData,
        toastMessage,
        null,
        displayInfo,
        { baseBooking: booking, onSuccess, successToastMessage }
      );

      if (updatedBooking) {
        onUpdate(booking._id, { status: 'removed' });
        setDialogOpen(false);
        setConfirmDialogOpen(false);
        setPendingResource(null);
        setValidationResult(null);
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error updating resource:', error);
      // Concurrency conflicts already surface a yellow toast from the hook.
      if (!error?.isConcurrencyConflict) {
        toast.error('Fehler beim Aktualisieren des Raums');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmTimeAdjustment = async () => {
    if (pendingResource && validationResult) {
      if (pendingResource._id === null) {
        await performResourceRemoval(validationResult.adjustedStartTime);
      } else {
        await performResourceUpdate(pendingResource, validationResult.adjustedStartTime);
      }
    }
  };

  const handleCancelTimeAdjustment = () => {
    setConfirmDialogOpen(false);
    setPendingResource(null);
    setValidationResult(null);
  };

  const handleRemoveResource = async () => {
    setValidating(true);

    try {
      const validation = await validateResourceChange(booking._id, null);

      if (validation.needsTimeAdjustment) {
        if (validation.hasConflict) {
          toast.error('Der neue Zeitslot ohne Ressource ist bereits belegt');
          setValidating(false);
          return;
        }

        setPendingResource({ _id: null, name: 'Kein Raum' });
        setValidationResult(validation);
        setConfirmDialogOpen(true);
        setValidating(false);
      } else {
        setValidating(false);
        await performResourceRemoval(null);
      }
    } catch (error) {
      console.error('Error validating resource removal:', error);
      toast.error('Fehler beim Validieren der Raumentfernung');
      setValidating(false);
    }
  };

  const performResourceRemoval = async adjustedStartTime => {
    setUpdating(true);

    const timeChangeText = adjustedStartTime
      ? ` und die Startzeit wurde zu ${dayjs(adjustedStartTime).format('HH:mm')} angepasst`
      : '';

    const toastMessage = `Deine Anfrage für das Entfernen des Raums${timeChangeText} für ${booking.firstName} ${booking.lastName} wurde erfolgreich gespeichert und kann später in den offenen Anfragen bestätigt werden`;
    const successToastMessage = `Der Raum für ${booking.firstName} ${booking.lastName} wurde erfolgreich entfernt${timeChangeText}`;

    const displayInfo = {
      Typ: 'Raum entfernen',
      Kunde: `${booking.firstName} ${booking.lastName}`,
      Von: booking.resourceId?.name || 'Kein Raum',
      Nach: 'Kein Raum',
      Buchungsnummer: booking.bookingNumber,
    };

    if (adjustedStartTime) {
      displayInfo['Neue Startzeit'] = dayjs(adjustedStartTime).format('DD.MM.YYYY HH:mm');
    }

    const onSuccess = async () => {
      onUpdate(booking._id, { status: 'removed' });
    };

    try {
      const updateData = { resourceId: null };
      if (adjustedStartTime) {
        updateData.adjustedStartTime = adjustedStartTime;
      }

      const updatedBooking = await updateInternalBooking(
        booking._id,
        updateData,
        toastMessage,
        null,
        displayInfo,
        { baseBooking: booking, onSuccess, successToastMessage }
      );

      if (updatedBooking) {
        onUpdate(booking._id, { status: 'removed' });
        setDialogOpen(false);
        setConfirmDialogOpen(false);
        setPendingResource(null);
        setValidationResult(null);
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error removing resource:', error);
      toast.error('Fehler beim Entfernen des Raums');
    } finally {
      setUpdating(false);
    }
  };

  const currentResource =
    booking.resourceId && booking.resourceId.name ? booking.resourceId.name : null;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          pt: { xs: 0, md: 2 },
          cursor: 'default',
        }}
      >
        {currentResource ? (
          <LockIconWrapper
            right='InternalBooking:update'
            style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '4px' }}
          >
            <Box sx={{ width: '100%' }}>
              <Box
                className='resource-box-on-hover'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '2px solid',
                  borderColor: resourceColor,
                  color: 'text.primary',
                  backgroundColor: alpha(
                    resourceColor,
                    theme.palette.mode === 'light' ? 0.12 : 0.22
                  ),
                  borderRadius: '8px',
                  px: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  transition: 'background-color 0.3s ease',
                  cursor: canUpdateResource ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: canUpdateResource ? alpha(resourceColor, 0.3) : undefined,
                  },
                }}
                onClick={handleResourceClick}
              >
                <span>{currentResource}</span>
                {canUpdateResource && <EditIcon fontSize='small' />}
              </Box>
            </Box>
          </LockIconWrapper>
        ) : (
          <Box
            sx={{
              cursor: canUpdateResource ? 'pointer' : 'default',
              borderRadius: '8px',
              px: 2,
              py: 1.5,
              '&:hover': {
                backgroundColor: canUpdateResource ? 'action.hover' : undefined,
              },
            }}
            onClick={handleResourceClick}
          >
            <Typography variant='body2' color='text.secondary'>
              Kein Raum zugewiesen
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Raum auswählen</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              <ListItem>
                <ListItemButton onClick={handleRemoveResource} disabled={updating || validating}>
                  <ListItemText
                    primary='Kein Raum zuweisen'
                    secondary='Buchung ohne spezifischen Raum'
                  />
                </ListItemButton>
              </ListItem>
              {availableResources.map(resource => (
                <ListItem key={resource._id}>
                  <ListItemButton
                    onClick={() => handleResourceSelect(resource)}
                    selected={currentResource === resource.name}
                    disabled={updating || validating}
                  >
                    <ListItemText
                      primary={resource.name}
                      secondary={resource.description || 'Keine Beschreibung verfügbar'}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
          {validating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
              <Typography variant='body2' sx={{ ml: 2 }}>
                Validiere Raumänderung...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={updating || validating}>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialogOpen} onClose={handleCancelTimeAdjustment} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color='warning' />
            Startzeitanpassung erforderlich
          </Box>
        </DialogTitle>
        <DialogContent>
          {validationResult && (
            <>
              <Alert severity='warning' sx={{ mb: 2 }}>
                <AlertTitle>Ressourcen-Offset erfordert Zeitanpassung</AlertTitle>
                Die gewählte Ressource hat einen anderen Zeitversatz ({
                  validationResult.newOffset
                }{' '}
                Minuten) als die aktuelle Ressource ({validationResult.currentOffset} Minuten).
              </Alert>

              <Typography variant='body1' sx={{ mb: 2 }}>
                <strong>Aktuelle Buchung:</strong>
              </Typography>
              <Typography variant='body2' sx={{ ml: 2, mb: 1 }}>
                Raum: {booking.resourceId?.name || 'Kein Raum'}
              </Typography>
              <Typography variant='body2' sx={{ ml: 2, mb: 1 }}>
                Startzeit: {dayjs(booking.startTime).format('DD.MM.YYYY HH:mm')}
              </Typography>
              <Typography variant='body2' sx={{ ml: 2, mb: 2 }}>
                Endzeit: {dayjs(booking.endTime).format('DD.MM.YYYY HH:mm')}
              </Typography>

              <Typography variant='body1' sx={{ mb: 2 }}>
                <strong>Nach der Änderung:</strong>
              </Typography>
              <Typography variant='body2' sx={{ ml: 2, mb: 1 }}>
                Raum: {pendingResource?.name || 'Kein Raum'}
              </Typography>
              <Typography
                variant='body2'
                sx={{ ml: 2, mb: 1, color: 'warning.main', fontWeight: 'bold' }}
              >
                Startzeit: {dayjs(validationResult.adjustedStartTime).format('DD.MM.YYYY HH:mm')}
              </Typography>
              <Typography
                variant='body2'
                sx={{ ml: 2, mb: 2, color: 'warning.main', fontWeight: 'bold' }}
              >
                Endzeit: {dayjs(validationResult.adjustedEndTime).format('DD.MM.YYYY HH:mm')}
              </Typography>

              <Alert severity='info'>
                Möchtest du den Raum wechseln und die Startzeit automatisch anpassen?
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTimeAdjustment} disabled={updating}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmTimeAdjustment}
            variant='contained'
            color='warning'
            disabled={updating}
          >
            {updating ? <CircularProgress size={24} /> : 'Bestätigen und anpassen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ResourceSection;
